import React from "react";
import { Server, ShieldCheck, Activity, Cpu, HardDrive, Wifi, Radio } from "lucide-react";

export default function ServerRackVisualizer() {
  const racks = [
    {
      id: "RACK-ZH01",
      name: "ZRH-T4-ALPHA",
      status: "ONLINE",
      load: "34%",
      temp: "18.4°C",
      units: [
        { u: "U42-40", type: "EDGE ROUTER 100G", leds: ["emerald", "emerald", "blue", "emerald"], activity: "fast" },
        { u: "U39-36", type: "FIREWALL & DDOS MITIGATION", leds: ["emerald", "emerald", "emerald", "amber"], activity: "pulse" },
        { u: "U35-30", type: "NVME STORAGE CLUSTER A", leds: ["blue", "blue", "emerald", "blue"], activity: "fast" },
        { u: "U29-24", type: "NVME STORAGE CLUSTER B", leds: ["blue", "emerald", "blue", "emerald"], activity: "medium" },
        { u: "U23-18", type: "COMPUTE NODE CH1", leds: ["emerald", "emerald", "emerald", "emerald"], activity: "slow" },
        { u: "U17-12", type: "COMPUTE NODE CH2", leds: ["emerald", "blue", "emerald", "emerald"], activity: "fast" },
        { u: "U11-06", type: "BACKUP & REPLICATION NODE", leds: ["emerald", "amber", "emerald", "blue"], activity: "slow" },
        { u: "U05-01", type: "REDUNDANT UPS & POWER MATRIX", leds: ["emerald", "emerald", "emerald", "emerald"], activity: "steady" },
      ]
    },
    {
      id: "RACK-ZH02",
      name: "ZRH-T4-BETA",
      status: "OPTIMAL",
      load: "28%",
      temp: "17.9°C",
      units: [
        { u: "U42-40", type: "CORE SWITCH ARISTA 400G", leds: ["emerald", "blue", "blue", "emerald"], activity: "fast" },
        { u: "U39-36", type: "KUBERNETES CONTROL MATRIX", leds: ["emerald", "emerald", "emerald", "blue"], activity: "fast" },
        { u: "U35-30", type: "HIGH-IOPS NVME POOL 01", leds: ["blue", "blue", "blue", "emerald"], activity: "fast" },
        { u: "U29-24", type: "HIGH-IOPS NVME POOL 02", leds: ["blue", "emerald", "blue", "blue"], activity: "fast" },
        { u: "U23-18", type: "CLOUD INSTANCE RUNNER #4", leds: ["emerald", "emerald", "blue", "emerald"], activity: "medium" },
        { u: "U17-12", type: "CLOUD INSTANCE RUNNER #5", leds: ["emerald", "emerald", "emerald", "emerald"], activity: "medium" },
        { u: "U11-06", type: "OFFSITE SNAPSHOT REPLICA", leds: ["blue", "emerald", "emerald", "blue"], activity: "slow" },
        { u: "U05-01", type: "DUAL A+B ATS POWER FEED", leds: ["emerald", "emerald", "emerald", "emerald"], activity: "steady" },
      ]
    }
  ];

  return (
    <div className="relative w-full overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-slate-950 via-[#0a0d14] to-black p-4 sm:p-5 shadow-2xl backdrop-blur-xl group">
      {/* Background Cyber Ambient Lights */}
      <div className="absolute -top-24 -right-24 h-56 w-56 rounded-full bg-red-600/15 blur-3xl pointer-events-none animate-pulse" />
      <div className="absolute -bottom-24 -left-24 h-56 w-56 rounded-full bg-blue-600/15 blur-3xl pointer-events-none animate-pulse" />
      <div className="absolute inset-0 bg-[radial-gradient(#ffffff08_1px,transparent_1px)] [background-size:16px_16px] pointer-events-none opacity-40" />

      {/* Header telemetry / status badge */}
      <div className="relative z-10 flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-white/[0.08]">
        <div className="flex items-center gap-2.5">
          <div className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[12px] font-bold uppercase tracking-wider text-white">Tier-IV Datacenter Zürich</span>
              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-500/10 text-red-400 border border-red-500/20">LIVE TELEMETRY</span>
            </div>
            <p className="text-[10px] text-slate-400 font-mono">NODE: RCK-ZH4-PROD • 99.99% UPTIME</p>
          </div>
        </div>

        <div className="flex items-center gap-3 text-[11px] font-mono text-slate-300">
          <div className="flex items-center gap-1.5 bg-white/[0.04] px-2 py-1 rounded-lg border border-white/[0.06]">
            <Activity className="h-3.5 w-3.5 text-emerald-400 animate-pulse" />
            <span>&lt; 0.8ms</span>
          </div>
          <div className="flex items-center gap-1.5 bg-white/[0.04] px-2 py-1 rounded-lg border border-white/[0.06]">
            <Radio className="h-3.5 w-3.5 text-blue-400 animate-pulse" />
            <span>400 Gbps</span>
          </div>
        </div>
      </div>

      {/* Main Server Cabinet Isometric Showcase */}
      <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-3.5 pt-4">
        {racks.map((rack) => (
          <div
            key={rack.id}
            className="relative rounded-xl border border-white/[0.12] bg-[#05070b]/90 p-3 shadow-inner flex flex-col justify-between overflow-hidden"
          >
            {/* Top Rack Mount Header */}
            <div className="flex items-center justify-between mb-2 pb-1.5 border-b border-white/[0.07] text-[10px] font-mono">
              <span className="text-slate-400 font-semibold flex items-center gap-1">
                <Server className="h-3 w-3 text-red-500" />
                {rack.name}
              </span>
              <span className="text-emerald-400 font-bold bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20">
                {rack.status}
              </span>
            </div>

            {/* Server Rack Units (Chassis stack) */}
            <div className="space-y-1.5">
              {rack.units.map((unit, uIdx) => (
                <div
                  key={uIdx}
                  className="relative group/unit flex items-center justify-between rounded-md border border-white/[0.07] bg-gradient-to-r from-slate-900 via-slate-950 to-slate-900 px-2 py-1.5 hover:border-red-500/40 hover:bg-slate-900/90 transition-all duration-300"
                >
                  {/* Subtle active laser bar */}
                  <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-red-500/60 rounded-l" />

                  {/* Left: Unit ID and Label */}
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[9px] font-mono text-slate-500 w-10 flex-shrink-0">{unit.u}</span>
                    <span className="text-[10px] font-medium text-slate-200 truncate group-hover/unit:text-white transition-colors">
                      {unit.type}
                    </span>
                  </div>

                  {/* Right: Blinking Server LEDs and Fiber Indicators */}
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {/* Simulated drive bays / IO strips */}
                    <div className="hidden sm:flex items-center gap-0.5 opacity-50 group-hover/unit:opacity-90 transition-opacity">
                      <span className="h-1.5 w-1 bg-white/30 rounded-xs" />
                      <span className="h-1.5 w-1 bg-white/30 rounded-xs" />
                      <span className="h-1.5 w-1 bg-white/30 rounded-xs" />
                    </div>

                    {/* Dynamic LED Array */}
                    <div className="flex items-center gap-1">
                      {unit.leds.map((color, lIdx) => {
                        let dotClass = "bg-emerald-400 shadow-[0_0_8px_#34d399]";
                        if (color === "blue") dotClass = "bg-sky-400 shadow-[0_0_8px_#38bdf8]";
                        if (color === "amber") dotClass = "bg-amber-400 shadow-[0_0_8px_#fbbf24]";

                        const animDuration =
                          unit.activity === "fast"
                            ? `${0.4 + (lIdx * 0.15)}s`
                            : unit.activity === "pulse"
                            ? "1.2s"
                            : `${0.8 + (lIdx * 0.2)}s`;

                        return (
                          <span
                            key={lIdx}
                            style={{ animationDuration: animDuration }}
                            className={`inline-block h-1.5 w-1.5 rounded-full animate-ping ${dotClass}`}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom Telemetry Bar */}
            <div className="mt-2.5 pt-1.5 border-t border-white/[0.06] flex items-center justify-between text-[9px] font-mono text-slate-400">
              <span className="flex items-center gap-1">
                <Cpu className="h-2.5 w-2.5 text-sky-400" />
                CPU: {rack.load}
              </span>
              <span className="flex items-center gap-1">
                <HardDrive className="h-2.5 w-2.5 text-emerald-400" />
                TEMP: {rack.temp}
              </span>
              <span className="text-slate-500">HOT-SWAP RAID-10</span>
            </div>
          </div>
        ))}
      </div>

      {/* Animated Bottom Optical Fiber Stream (MeoHost aesthetic) */}
      <div className="relative z-10 mt-3 pt-2.5 border-t border-white/[0.08] flex items-center justify-between text-[10px] text-slate-400">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
          <span className="font-semibold text-slate-300">Swiss Datacenter Security & ISO 27001</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="hidden sm:inline font-mono text-[9px] text-slate-500">FIBER SYNC:</span>
          <div className="flex gap-1">
            <span className="h-1.5 w-6 rounded-full bg-emerald-500/40 animate-pulse" />
            <span className="h-1.5 w-4 rounded-full bg-red-500/50 animate-pulse" />
            <span className="h-1.5 w-8 rounded-full bg-blue-500/40 animate-pulse" />
          </div>
        </div>
      </div>
    </div>
  );
}
